import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, Trash2, Plus } from 'lucide-react';
import * as tareasApi from '@/api/tareas-cliente.api';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { PageHeader } from '@/components/common/PageHeader';
import { Cargando } from '@/components/common/Cargando';
import { useGuia } from '@/components/guia/guia-contexto';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/cn';
import { armarRespuestasDeModelos, llenarVacios, mesesValidos } from '@/lib/tareas-cliente';

/**
 * "LO QUE FALTA CONFIGURAR".
 *
 * Arriba, lo que el sistema necesita para trabajar completo. Nadie lo tacha a
 * mano: cada tarea se da por hecha sola cuando el dato ya está cargado, así la
 * lista nunca dice "listo" con el dato faltando.
 *
 * Abajo, las notas propias: lo que el negocio quiera ir anotando y tachando.
 *
 * Cuando ya no hace falta, la tarjeta del resumen se oculta desde acá.
 */
export default function Tareas() {
  const qc = useQueryClient();
  const toast = useToast();
  const guia = useGuia();
  const navigate = useNavigate();
  const [modelosAbierto, setModelosAbierto] = useState(false);
  const [nueva, setNueva] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['tareas-cliente'],
    queryFn: tareasApi.obtener,
  });
  const refrescar = () => void qc.invalidateQueries({ queryKey: ['tareas-cliente'] });

  const agregar = useMutation({
    mutationFn: (titulo: string) => tareasApi.agregarPersonal(titulo),
    onSuccess: () => {
      setNueva('');
      refrescar();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const marcar = useMutation({
    mutationFn: ({ id, hecho }: { id: string; hecho: boolean }) =>
      tareasApi.marcarPersonal(id, hecho),
    onSuccess: refrescar,
    onError: (e: Error) => toast.error(e.message),
  });
  const borrar = useMutation({
    mutationFn: tareasApi.borrarPersonal,
    onSuccess: refrescar,
    onError: (e: Error) => toast.error(e.message),
  });
  const ocultar = useMutation({
    mutationFn: tareasApi.ocultar,
    onSuccess: (_r, oculto) => {
      toast.success(
        oculto ? 'La tarjeta ya no aparece en el resumen' : 'La tarjeta vuelve al resumen',
      );
      refrescar();
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const mapas = useMutation({
    mutationFn: tareasApi.decidirMapas,
    onSuccess: () => {
      toast.success('Decisión guardada');
      refrescar();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading || !data) return <Cargando />;

  function actuar(t: tareasApi.TareaCliente) {
    if (t.accion === 'modelos') setModelosAbierto(true);
    else if (t.accion === 'zonas') navigate('/zonas');
    else if (t.accion === 'guia' && t.guia) guia.iniciar(t.guia);
  }

  const { hechas, total } = data.resumen;

  return (
    <div className="space-y-6 p-4 md:p-6">
      <PageHeader
        title="Lo que falta configurar"
        description="Lo que el sistema necesita saber de tu negocio para trabajar completo. Cada tarea se marca sola cuando el dato queda cargado."
      />

      <Card>
        <CardBody className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {hechas} de {total} listas
            </p>
            <Button
              variant="ghost"
              size="sm"
              loading={ocultar.isPending}
              onClick={() => ocultar.mutate(!data.oculto)}
            >
              {data.oculto ? 'Mostrar la tarjeta en el resumen' : 'Ocultar la tarjeta del resumen'}
            </Button>
          </div>

          <ul className="divide-y divide-surface-border dark:divide-slate-800">
            {data.tareas.map((t) => (
              <li key={t.id} className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start">
                {t.hecho ? (
                  <CheckCircle2 className="h-5 w-5 shrink-0 text-green-600" aria-label="Lista" />
                ) : (
                  <Circle className="h-5 w-5 shrink-0 text-slate-300" aria-label="Pendiente" />
                )}
                <div className="min-w-0 flex-1 space-y-1">
                  <p
                    className={cn(
                      'text-sm font-medium',
                      t.hecho
                        ? 'text-slate-400 line-through'
                        : 'text-slate-800 dark:text-slate-100',
                    )}
                  >
                    {t.titulo}
                  </p>
                  {!t.hecho && (
                    <>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{t.porque}</p>
                      {t.detalle && (
                        <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                          {t.detalle}
                        </p>
                      )}
                    </>
                  )}
                  {t.accion === 'mapas' && (
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={mapas.isPending}
                        onClick={() => mapas.mutate('manual')}
                      >
                        A mano (gratis)
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={mapas.isPending}
                        onClick={() => mapas.mutate('automatico')}
                      >
                        Automático (se paga)
                      </Button>
                    </div>
                  )}
                  {t.accion === 'mapas' && !t.hecho && (
                    <p className="text-xs text-slate-400">
                      Si eliges automático, el equipo de soporte te contacta para activarlo: tiene
                      un costo por consulta.
                    </p>
                  )}
                </div>
                {!t.hecho && t.accion !== 'mapas' && (
                  <Button
                    size="sm"
                    className="self-start"
                    disabled={t.accion === 'guia' && !!t.guia && !guia.puedeUsar(t.guia)}
                    onClick={() => actuar(t)}
                  >
                    {t.accion === 'guia' ? 'Muéstrame' : 'Completar'}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-4">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Mis notas
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Anota lo que te haga falta y táchalo cuando esté hecho.
            </p>
          </div>

          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (nueva.trim()) agregar.mutate(nueva.trim());
            }}
          >
            <Input
              id="tareas-nueva-nota"
              value={nueva}
              maxLength={200}
              onChange={(e) => setNueva(e.target.value)}
              placeholder="Ej.: Confirmar el teléfono de Rafael para los avisos"
              aria-label="Nueva nota"
            />
            <Button type="submit" loading={agregar.isPending} aria-label="Agregar nota">
              <Plus className="h-4 w-4" />
            </Button>
          </form>

          {data.personales.length === 0 ? (
            <p className="text-sm text-slate-400">Todavía no hay notas.</p>
          ) : (
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {data.personales.map((p) => (
                <li key={p.id} className="flex items-start gap-3 py-3">
                  <input
                    id={`nota-${p.id}`}
                    type="checkbox"
                    checked={p.hecho}
                    onChange={(e) => marcar.mutate({ id: p.id, hecho: e.target.checked })}
                    className="mt-0.5 h-4 w-4 accent-brand-600"
                  />
                  <label htmlFor={`nota-${p.id}`} className="min-w-0 flex-1 cursor-pointer">
                    <span
                      className={cn(
                        'block text-sm',
                        p.hecho
                          ? 'text-slate-400 line-through'
                          : 'text-slate-800 dark:text-slate-100',
                      )}
                    >
                      {p.titulo}
                    </span>
                    {p.detalle && <span className="block text-xs text-slate-500">{p.detalle}</span>}
                    <span className="block text-xs text-slate-400">Anotada por {p.creadaPor}</span>
                  </label>
                  <button
                    type="button"
                    aria-label={`Borrar la nota ${p.titulo}`}
                    onClick={() => borrar.mutate(p.id)}
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600 dark:hover:bg-slate-800"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <ModalModelos
        abierto={modelosAbierto}
        onCerrar={() => setModelosAbierto(false)}
        pendientes={data.modelosPorConfirmar}
        onListo={refrescar}
      />
    </div>
  );
}

function ModalModelos({
  abierto,
  onCerrar,
  pendientes,
  onListo,
}: {
  abierto: boolean;
  onCerrar: () => void;
  pendientes: string[];
  onListo: () => void;
}) {
  const toast = useToast();
  const [meses, setMeses] = useState<Record<string, string>>({});
  const [noEsModelo, setNoEsModelo] = useState<Record<string, boolean>>({});

  const respuestas = armarRespuestasDeModelos(pendientes, meses, noEsModelo);
  const guardar = useMutation({
    mutationFn: () => tareasApi.responderModelos(respuestas),
    onSuccess: (r) => {
      toast.success(
        r.quedan === 0
          ? 'Todos los modelos quedaron cargados'
          : `Guardado. Quedan ${r.quedan} por completar`,
      );
      setMeses({});
      setNoEsModelo({});
      onListo();
      if (r.quedan === 0) onCerrar();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Modal
      open={abierto}
      onClose={onCerrar}
      size="xl"
      title="Meses de garantía de tus modelos"
      description="Escribe cuántos meses cubre la garantía de cada uno. Puedes guardar los que ya sepas y volver por el resto."
      footer={
        <>
          <Button variant="ghost" onClick={onCerrar}>
            Cerrar
          </Button>
          <Button
            disabled={respuestas.length === 0}
            loading={guardar.isPending}
            onClick={() => guardar.mutate()}
          >
            Guardar {respuestas.length > 0 ? `(${respuestas.length})` : ''}
          </Button>
        </>
      }
    >
      {pendientes.length === 0 ? (
        <p className="text-sm text-slate-500">No hay modelos esperando sus meses.</p>
      ) : (
        <div className="space-y-3">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setMeses(llenarVacios(pendientes, meses, noEsModelo, '12'))}
          >
            Poner 12 meses en los que están vacíos
          </Button>
          <ul className="divide-y divide-surface-border dark:divide-slate-800">
            {pendientes.map((nombre, i) => {
              const valor = meses[nombre] ?? '';
              const invalido = valor.trim() !== '' && mesesValidos(valor) === null;
              return (
                <li key={nombre} className="flex flex-wrap items-center gap-3 py-2">
                  <span className="min-w-0 flex-1 text-sm text-slate-800 dark:text-slate-100">
                    {nombre}
                  </span>
                  <input
                    id={`modelo-meses-${i}`}
                    inputMode="numeric"
                    value={valor}
                    disabled={noEsModelo[nombre]}
                    onChange={(e) => setMeses((m) => ({ ...m, [nombre]: e.target.value }))}
                    aria-label={`Meses de garantía de ${nombre}`}
                    aria-invalid={invalido}
                    placeholder="meses"
                    className={cn(
                      'h-9 w-20 rounded-md border bg-transparent px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50',
                      invalido ? 'border-red-500' : 'border-surface-border dark:border-slate-700',
                    )}
                  />
                  <label className="flex items-center gap-1.5 text-xs text-slate-500">
                    <input
                      id={`modelo-no-es-${i}`}
                      type="checkbox"
                      checked={Boolean(noEsModelo[nombre])}
                      onChange={(e) => setNoEsModelo((n) => ({ ...n, [nombre]: e.target.checked }))}
                      className="h-4 w-4 accent-brand-600"
                    />
                    No es un modelo
                  </label>
                </li>
              );
            })}
          </ul>
          <p className="text-xs text-slate-400">
            Los meses van de 1 a 120. Lo que quede vacío no se guarda: sigue esperando.
          </p>
        </div>
      )}
    </Modal>
  );
}
